"use client"

import { useState, useCallback, useMemo, useEffect } from "react"
import type { CardPosition } from "@/components/tour/tour-card"

export interface TourStep {
  id: string
  title: string
  description: string
  descriptionMobile?: string // Optional mobile-specific description
  targetIds?: string[] // DOM element IDs to highlight (tries each until found)
  advancedOnly?: boolean // Only show in advanced/full tour mode
  cardPosition?: CardPosition // Where to position the tour card relative to target
  // Actions to trigger when step is shown
  action?: "selectHero" | "openTowerMenu" | "selectTower" | "openDroppodMenu"
}

// All tour steps - desktop descriptions by default, mobile override where needed
const allTourSteps: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to PELF!",
    description: "Your mission: Defend your droppod from waves of alien invaders! Build towers, upgrade your hero, and survive as long as you can.",
    descriptionMobile: "Defend your droppod from aliens! Build towers, upgrade your hero, and survive!",
    cardPosition: "center",
  },
  {
    id: "skill-points-intro",
    title: "Skill Points",
    description: "You have unspent skill points! Click the glowing button to open your skill menu and power up your hero.",
    descriptionMobile: "You have skill points! Tap the glowing button to upgrade your hero's abilities.",
    targetIds: ["skill-upgrade-button"],
    cardPosition: "left",
  },
  {
    id: "allocate-skill",
    title: "Choose a Skill",
    description: "Click on a skill to upgrade it. Each skill has multiple levels and unique effects. Choose wisely!",
    descriptionMobile: "Tap a skill to upgrade it. Each skill has multiple levels with powerful effects!",
    targetIds: ["skill-upgrade-menu"],
    advancedOnly: true,
    cardPosition: "left",
  },
  {
    id: "hero-movement",
    title: "Move Your Hero",
    description: "Right-click anywhere on the map to move your hero. Your hero attacks nearby enemies automatically.",
    descriptionMobile: "Tap and hold, then release to move your hero. Your hero attacks enemies automatically.",
    action: "selectHero",
    cardPosition: "below",
  },
  {
    id: "build-tower-intro",
    title: "Build Towers",
    description: "Click on any glowing build tile to open the tower menu. Build towers to help defend against waves of enemies!",
    descriptionMobile: "Tap a glowing build tile to open the tower menu. Build towers to defend!",
    targetIds: ["tower-menu"],
    action: "openTowerMenu",
    cardPosition: "above",
  },
  {
    id: "select-archer",
    title: "Build an Archer Tower",
    description: "Select the Archer Tower - it's cheap, has good range, and is perfect for beginners. Click to build it!",
    descriptionMobile: "Select the Archer Tower - cheap, good range, perfect for beginners. Tap once to select, tap again to build!",
    targetIds: ["tower-menu-archer"],
    cardPosition: "above",
  },
  {
    id: "upgrade-tower",
    title: "Select Tower",
    description: "Click on your tower to select it. You can then upgrade it to increase damage and range, or sell it for gold.",
    descriptionMobile: "Tap your tower to select it. You can upgrade it for more power, or sell it for gold.",
    targetIds: ["tower-info-panel"],
    action: "selectTower",
    advancedOnly: true,
    cardPosition: "below",
  },
  {
    id: "droppod-menu",
    title: "Your Droppod",
    description: "Click on your droppod (the ship you landed in) to access cloning, the upgrade tree, and the Phase Terminal shop.",
    descriptionMobile: "Tap your droppod to clone your hero, access upgrades, and shop at the Phase Terminal.",
    targetIds: ["droppod-menu"],
    action: "openDroppodMenu",
    advancedOnly: true,
    cardPosition: "below",
  },
  {
    id: "start-wave",
    title: "Start the Wave!",
    description: "Ready? Click the 'Call Wave' button to begin the first wave of enemies. Good luck, Commander!",
    descriptionMobile: "Ready? Tap 'Call Wave' to start the first wave. Good luck, Commander!",
    targetIds: ["start-wave-button", "start-wave-button-docked", "next-wave-button-container"],
    cardPosition: "below",
  },
]

// Quick start tour shows only essential steps
const quickStartStepIds = [
  "welcome",
  "skill-points-intro",
  "hero-movement",
  "build-tower-intro",
  "select-archer",
  "start-wave",
]

export interface UseTourProps {
  isMobile: boolean
  onSelectHero?: () => void
  onOpenTowerMenu?: () => void
  onSelectTower?: () => void
  onOpenDroppodMenu?: () => void
  onCleanupStep?: () => void // Called when leaving a step to clean up
}

export interface UseTourReturn {
  // Display states
  showTourButton: boolean
  tourActive: boolean
  inTourMode: boolean
  showIntroCard: boolean

  // Navigation
  tourStep: number
  maxTourSteps: number
  tourSteps: TourStep[]

  // Current step data
  currentStepData: TourStep | null
  getDescription: (step: TourStep) => string

  // Handlers
  handleStartTour: () => void
  handleStartTourFromIntro: (mode: "quick" | "full") => void
  handleCloseIntroCard: () => void
  handleTourNext: () => void
  handleTourPrevious: () => void
  handleTourClose: () => void
  handleDismissTourButton: () => void
}

const TOUR_DISMISSED_KEY = "pelf_tour_dismissed"
const TOUR_COMPLETED_KEY = "pelf_tour_completed"

export function useTour({
  isMobile,
  onSelectHero,
  onOpenTowerMenu,
  onSelectTower,
  onOpenDroppodMenu,
  onCleanupStep,
}: UseTourProps): UseTourReturn {
  const [showTourButton, setShowTourButton] = useState(false)
  const [showIntroCard, setShowIntroCard] = useState(false)
  const [tourActive, setTourActive] = useState(false)
  const [tourStep, setTourStep] = useState(0)
  const [tourMode, setTourMode] = useState<"quick" | "full">("quick")

  // Check localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem(TOUR_DISMISSED_KEY)
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY)
    if (!dismissed && !completed) {
      // Show tour button after a short delay
      const timer = setTimeout(() => {
        setShowTourButton(true)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [])

  // Filter steps based on tour mode
  const tourSteps = useMemo(() => {
    if (tourMode === "quick") {
      return allTourSteps.filter((step) => quickStartStepIds.includes(step.id))
    }
    return allTourSteps.filter((step) => !step.advancedOnly || tourMode === "full")
  }, [tourMode])

  const maxTourSteps = tourSteps.length
  const currentStepData = tourActive && tourStep < tourSteps.length ? tourSteps[tourStep] : null
  const inTourMode = showIntroCard || tourActive

  // Get appropriate description for platform
  const getDescription = useCallback(
    (step: TourStep): string => {
      if (isMobile && step.descriptionMobile) {
        return step.descriptionMobile
      }
      return step.description
    },
    [isMobile]
  )

  // Trigger step-specific actions
  useEffect(() => {
    if (!currentStepData) return

    // Small delay to let previous step cleanup
    const timer = setTimeout(() => {
      switch (currentStepData.action) {
        case "selectHero":
          onSelectHero?.()
          break
        case "openTowerMenu":
          onOpenTowerMenu?.()
          break
        case "selectTower":
          onSelectTower?.()
          break
        case "openDroppodMenu":
          onOpenDroppodMenu?.()
          break
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [currentStepData, onSelectHero, onOpenTowerMenu, onSelectTower, onOpenDroppodMenu])

  // Keyboard navigation
  useEffect(() => {
    if (!tourActive) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault()
        handleTourNext()
      } else if (e.key === "ArrowLeft") {
        e.preventDefault()
        handleTourPrevious()
      } else if (e.key === "Escape") {
        e.preventDefault()
        handleTourClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [tourActive, tourStep, maxTourSteps])

  const handleStartTour = useCallback(() => {
    setShowTourButton(false)
    setShowIntroCard(true)
  }, [])

  const handleStartTourFromIntro = useCallback((mode: "quick" | "full") => {
    setTourMode(mode)
    setShowIntroCard(false)
    setTourStep(0)
    setTourActive(true)
  }, [])

  const handleCloseIntroCard = useCallback(() => {
    setShowIntroCard(false)
    setShowTourButton(true)
  }, [])

  const handleTourNext = useCallback(() => {
    // Cleanup current step before advancing
    onCleanupStep?.()

    if (tourStep < maxTourSteps - 1) {
      setTourStep((prev) => prev + 1)
    } else {
      // Tour complete
      setTourActive(false)
      localStorage.setItem(TOUR_COMPLETED_KEY, "true")
    }
  }, [tourStep, maxTourSteps, onCleanupStep])

  const handleTourPrevious = useCallback(() => {
    // Cleanup current step before going back
    onCleanupStep?.()

    if (tourStep > 0) {
      setTourStep((prev) => prev - 1)
    }
  }, [tourStep, onCleanupStep])

  const handleTourClose = useCallback(() => {
    onCleanupStep?.()
    setTourActive(false)
    setShowTourButton(false)
  }, [onCleanupStep])

  const handleDismissTourButton = useCallback(() => {
    setShowTourButton(false)
    localStorage.setItem(TOUR_DISMISSED_KEY, "true")
  }, [])

  return {
    showTourButton,
    tourActive,
    inTourMode,
    showIntroCard,
    tourStep,
    maxTourSteps,
    tourSteps,
    currentStepData,
    getDescription,
    handleStartTour,
    handleStartTourFromIntro,
    handleCloseIntroCard,
    handleTourNext,
    handleTourPrevious,
    handleTourClose,
    handleDismissTourButton,
  }
}
